import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, XCircle, FileEdit } from 'lucide-react';
import { platformLabel } from '@/lib/clipPlatformFormats';

const STATUS_META = {
  draft: { label: 'Draft', icon: FileEdit, cls: 'bg-white/10 text-[#C0C7D4]' },
  pending_review: { label: 'Pending Review', icon: Clock, cls: 'bg-amber-500/15 text-amber-300' },
  approved: { label: 'Approved', icon: CheckCircle2, cls: 'bg-green-500/15 text-green-300' },
  rejected: { label: 'Rejected', icon: XCircle, cls: 'bg-red-500/15 text-red-300' },
};
const NEXT_STATUS = { draft: 'pending_review', pending_review: 'approved', approved: 'draft', rejected: 'draft' };

/**
 * Review — each platform variant of the active design keeps its own
 * independent status. There is no backend "image post" approval API yet
 * (only Editor Clips have one), so this is explicitly local/Level 1-2 —
 * never implied as server-persisted.
 */
export function DesignReviewPanel({ variants, onCycleStatus, onSelectVariant, activeVariantId, onReject }) {
  if (!variants || variants.length === 0) {
    return <p className="text-sm text-[#8A93A6] text-center py-6" data-testid="design-review-empty">Create a design to review its variants here.</p>;
  }
  return (
    <div className="space-y-2" data-testid="design-review-panel">
      <p className="text-[11px] text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-md p-2">Status shown here is local to this browser — not yet saved to the server (Level 3).</p>
      {variants.map((v) => {
        const meta = STATUS_META[v.approvalStatus] || STATUS_META.draft;
        const Icon = meta.icon;
        return (
          <div key={v.id} onClick={() => onSelectVariant(v.id)} className={`p-2.5 rounded-lg border cursor-pointer ${v.id === activeVariantId ? 'border-[#3A7BFF] bg-[#3A7BFF]/10' : 'border-white/10 hover:bg-white/5'}`} data-testid={`design-review-item-${v.id}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-white font-medium">{platformLabel(v.platform)}</span>
              <Badge className={`${meta.cls} border-0 text-[10px] flex items-center gap-1`}><Icon className="h-2.5 w-2.5" />{meta.label}</Badge>
            </div>
            <p className="text-[10px] text-[#8A93A6] mb-1.5">{v.aspectRatio} · {v.pages.length} page{v.pages.length === 1 ? '' : 's'}</p>
            <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
              <Button size="sm" variant="outline" className="h-6 text-[10px] border-white/10 flex-1" onClick={() => onCycleStatus(v.id, NEXT_STATUS[v.approvalStatus] || 'pending_review')} data-testid={`design-review-advance-${v.id}`}>
                {v.approvalStatus === 'approved' ? 'Reset to Draft' : v.approvalStatus === 'pending_review' ? 'Approve' : 'Submit for Review'}
              </Button>
              {v.approvalStatus !== 'rejected' && (
                <Button size="sm" variant="outline" className="h-6 text-[10px] border-red-500/30 text-red-400" onClick={() => onReject(v.id)} data-testid={`design-review-reject-${v.id}`}>Reject</Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
